import React from 'react';
import styled from 'styled-components';

const MODAL_START_Z_INDEX = 1000;

const ModalContent = styled.div`
  position: relative;
  max-width: 100%;
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const ModalBackground = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.35);
  left: 0;
  top: 0;
`;

const ModalWrapper = styled.div<{ zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;

  z-index: ${({ zIndex }) => MODAL_START_Z_INDEX + (zIndex ?? 0)};
`;

type ContextType = {
  openModal: (key: string, modalComponent: React.FC, props: any) => void;
  updateProps: (key: string, props: any) => void;
  closeModal: (key: string) => void;
};

const initialState: ContextType = {
  openModal: () => {},
  updateProps: () => {},
  closeModal: () => {},
};

const ModalPropviderContext = React.createContext(initialState);
export const useModalProvider = () => React.useContext(ModalPropviderContext);

interface ActiveModals {
  [key: string]: { component: React.FC; props: any } | undefined;
}

export const ModalProvider = ({ children }: any) => {
  const [activeModals, setActiveModals] = React.useState<ActiveModals>({});

  const openModal = (key: string, modalComponent: React.FC, props: any) => {
    setActiveModals((prev) => {
      return {
        ...prev,
        [key]: { component: modalComponent, props: props },
      };
    });
  };

  const updateProps = (key: string, props: any) => {
    if (!activeModals?.[key]) return;

    setActiveModals((prev) => {
      return {
        ...prev,
        [key]: {
          component: activeModals?.[key]?.component as React.FC,
          props: {
            ...activeModals?.[key]?.props,
            ...props,
          },
        },
      };
    });
  };

  const closeModal = (key: string) => {
    setActiveModals((prev) => {
      if (key in prev) {
        delete prev[key];
      }

      return { ...prev };
    });
  };

  return (
    <ModalPropviderContext.Provider value={{ openModal, updateProps, closeModal }}>
      {children}
      {activeModals &&
        Object.keys(activeModals).map((key: string, index: number) => {
          return activeModals[key] ? (
            <ModalWrapper key={key} zIndex={index}>
              <ModalBackground onClick={() => closeModal(key)} />
              <ModalContent>
                {React.createElement(activeModals?.[key]?.component as React.FC, {
                  close: () => closeModal(key),
                  key: key,
                  ...activeModals?.[key]?.props,
                })}
              </ModalContent>
            </ModalWrapper>
          ) : (
            <></>
          );
        })}
    </ModalPropviderContext.Provider>
  );
};

export interface ModalParentInterface {
  close(): void;
}
